<template>
    <div class="container-fluid">
        <h1>{{ title }}</h1>
        <br />
        <b-form
            ref="connectionLicenseForm"
            name="connectionLicenseForm"
            v-on:submit.prevent="$_onSubmit"
            class="pb-5"
        >
            <div
                class="col-md-6"
                v-if="
                    connectionLicense && connectionLicense.urlConnectionLicense
                "
            >
                <div class="form-group">
                    <!--Type-->
                    <label class="d-block">{{ $t('columnTypeTitle') }}</label>
                    <label class="font-weight-bold d-block">{{
                        $_getTypeLabel(
                            connectionLicense.urlConnectionLicense.type
                        )
                    }}</label>

                    <!--Name-->
                    <label class="pt-2 d-block">{{ $t('labelName') }}</label>
                    <label class="font-weight-bold d-block">{{
                        connectionLicense.urlConnectionLicense.name
                    }}</label>

                    <!--Beschikbaar-->
                    <x-checkbox
                        class="pt-3 d-block"
                        :name="'chkConnectionAvailable'"
                        :disabled="false"
                        v-model="connectionLicense.available"
                        :text="$t('labelAvailable')"
                    >
                    </x-checkbox>

                    <!--Link-->
                    <label class="pt-3 d-block">{{
                        $t('columnLinkText')
                    }}</label>
                    <x-input
                        id="connectionLinkText"
                        :state="!linkTextExists"
                        required
                        v-model="connectionLicense.linkText"
                        @input="$_checklinkTextExists"
                        :invalidMessage="$t('linkTextInUseErrorMessage')"
                    >
                    </x-input>

                    <!--Url-->
                    <label class="pt-2 d-block">{{ $t('labelBaseUrl') }}</label>
                    <x-input
                        v-if="hasPlaceholderUrl"
                        id="connectionLicenseUrl"
                        type="text"
                        required
                        v-model="connectionLicense.url"
                    >
                    </x-input>
                    <label
                        v-if="!hasPlaceholderUrl"
                        class="font-weight-bold d-block"
                        >{{ $t('notApplicable') }}</label
                    >

                    <!--Token-->
                    <label class="pt-2 d-block">{{
                        $t('columnNameToken')
                    }}</label>
                    <x-input
                        v-if="hasPlaceholderToken"
                        id="connectionLicenseToken"
                        type="text"
                        required
                        v-model="connectionLicense.token"
                    >
                    </x-input>
                    <label
                        v-if="!hasPlaceholderToken"
                        class="font-weight-bold d-block"
                        >{{ $t('notApplicable') }}</label
                    >

                    <!--Complete URL-->
                    <label class="pt-2 d-block"
                        >Complete {{ $t('columnUriTitle') }}</label
                    >
                    <label class="font-weight-bold d-block">{{
                        getCompleteUrl
                    }}</label>

                    <!--Username and Password-->
                    <div v-if="$_hasUsernameAndPassword">
                        <!--Username-->
                        <label class="pt-3 d-block">{{
                            $t('labelUser')
                        }}</label>
                        <x-input
                            id="connectionLicenseUsername"
                            type="text"
                            required
                            v-model="connectionLicense.username"
                        >
                        </x-input>

                        <!--Password-->
                        <label class="d-block">{{ $t('labelPassword') }}</label>
                        <x-input
                            id="connectionLicensePassword"
                            type="text"
                            required
                            v-model="connectionLicense.password"
                            @input="$_onChangePassword(row.item)"
                        >
                        </x-input>
                    </div>

                    <!-- Bitmap -->
                    <label class="pt-2 d-block">{{ $t('icon') }}</label>
                    <b-img
                        :src="$_getImageData(connectionLicense)"
                        fluid
                        alt="No icon"
                    >
                    </b-img>
                </div>

                <!-- Save/cancel/remove buttons -->
                <SubmitCancelRemove
                    :is-saving="isSaving"
                    :mode="mode"
                    :isBtnSaveDisabled="isBtnSaveDisabled"
                    @cancel="$_onCancel"
                >
                </SubmitCancelRemove>
            </div>
        </b-form>
    </div>
</template>

<script>
    /* Store imports */
    import { mapState } from 'vuex';
    import { URL_CONNECTION_TYPE } from '../../constants';
    import { nprogress, trimAsterisks } from '../../services/utilities';
    import xcheckbox from '../../components/BaseComponents/x-checkbox.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import Xinput from '../../components/BaseComponents/x-input.vue';

    export default {
        name: 'UrlConnectionLicensesDetail',
        mixins: [modalMixin],

        components: {
            SubmitCancelRemove,
            'x-checkbox': xcheckbox,
            'x-input': Xinput
        },

        props: {
            // url connection license id
            id: { type: Number, default: -1 }
        },

        data: function () {
            return {
                // Constants
                mode: 0,
                title: '',
                connectionLicense: {
                    name: '',
                    imageData: '',
                    type: 0,
                    url: '',
                    standardLicenced: false,
                    standardAvailable: false
                },
                isSaving: false,
                linkTextExists: false,
                connectionTypeOptions: [
                    { text: 'Beeldmateriaal ', value: 1 },
                    { text: 'Overig', value: 2 }
                ]
            };
        },

        computed: {
            ...mapState(['userInfo']),

            /**
             * Check if connection license can be saved
             * @returns {boolean} indicating if organization can be saved
             */
            isBtnSaveDisabled: function () {
                return this.isSaving;
            },

            /**
             * @returns {boolean} indicating if the placeholder is part of the URL
             */
            hasPlaceholderUrl: function () {
                return this.connectionLicense.urlConnectionLicense.url.includes(
                    '[URL]'
                );
            },

            /**
             * @returns {boolean} indicating if the placeholder is part of the URL
             */
            hasPlaceholderToken: function () {
                return this.connectionLicense.urlConnectionLicense.url.includes(
                    '[TOKEN]'
                );
            },

            /**
             * Returns the complete url with [URL] and [TOKEN] replace by the real values.
             * @param connection license
             */
            getCompleteUrl: function () {
                // Get the url.
                // If the base url contains a question mark, replace all question marks
                // in the connection license url with ampersands.
                const baseUrl = this.connectionLicense.url;
                let url = baseUrl.includes('?')
                    ? this.connectionLicense.urlConnectionLicense.url.replaceAll(
                          '?',
                          '&'
                      )
                    : this.connectionLicense.urlConnectionLicense.url;

                // Get the token if it exists.
                const token = this.connectionLicense.token
                    ? this.connectionLicense.token
                    : '';

                // Replace [URL], [TOKEN] and [USER]
                return url
                    .replace('[URL]', baseUrl)
                    .replace('[TOKEN]', token)
                    .replace('[USER]', this.userInfo.userName);
            },

            /**
             * Returns true if the connection name contains word "Kavel 10" but not "Vision10"
             * @param input Name of the url connection
             */
            $_hasUsernameAndPassword: function () {
                return (
                    this.connectionLicense.urlConnectionLicense.name.includes(
                        'Kavel10'
                    ) &&
                    !this.connectionLicense.urlConnectionLicense.name.includes(
                        'Vision10'
                    )
                );
            }
        },

        created() {
            if (this.id > 0) {
                this.title = this.$t('urlConnectionsDetailEditTitle');
                this.$_getConnectionLicense();
            }
        },

        methods: {
            async $_checklinkTextExists(input) {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetConnectionLinkTextExists',
                    parameters: {
                        linkText: input
                    }
                });
                this.linkTextExists = result?.data;
            },

            async $_getConnectionLicense() {
                try {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetUrlConnectionSettingsById',
                        parameters: {
                            urlConnectionId: this.id
                        }
                    });
                    this.connectionLicense = Object.assign({}, result?.data);
                    this.connectionLicense.imageData = Buffer.from(
                        this.connectionLicense.imageData,
                        'base64'
                    ).toString();
                } catch (error) {
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t('errorRetrievingConnection'),
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                }
            },

            $_goToOverview() {
                this.$router.push({ path: '/dashboard/UrlConnectionsOverview' });
            },

            $_onCancel: function () {
                this.$_goToOverview();
            },

            /**
             * Gets the type label based on the connection type
             * @param connectionType
             */
            $_getTypeLabel(connectionType) {
                if (connectionType === URL_CONNECTION_TYPE.StreetView) {
                    return this.$t('streetView');
                } else if (connectionType === URL_CONNECTION_TYPE.Others) {
                    return this.$t('others');
                }
                return '-';
            },

            /**
             * Retuns the (converted)image of the connection or connection license
             * @param input
             */
            $_getImageData(input) {
                // Convert the image for view, if necessary
                return input.imageData && !input.imageData.startsWith('data:')
                    ? Buffer.from(input.imageData, 'base64').toString()
                    : input.imageData;
            },

            /**
             * Trims out the '*' from input
             * @param item
             */
            $_onChangePassword(item) {
                item.passwordIsChanged = true;
                item.password = trimAsterisks(item.password);
            },

            async $_onSave() {
                const dataToSave = {
                    Id: this.connectionLicense.id,
                    ImageData: null,
                    OrganizationId: this.connectionLicense.organizationId,
                    Password: this.connectionLicense.password,
                    Url: this.connectionLicense.url,
                    UrlBitmapId: this.connectionLicense.urlBitmapId,
                    UrlConnectionLicense:
                        this.connectionLicense.urlConnectionLicense,
                    UrlConnectionLicenseId:
                        this.connectionLicense.urlConnectionLicenseId,
                    Username: this.connectionLicense.username,
                    Available: this.connectionLicense.available,
                    Licensed: this.connectionLicense.licensed,
                    LinkText: this.connectionLicense.linkText,
                    Token: this.connectionLicense.token,
                    PasswordIsChanged: this.connectionLicense.passwordIsChanged
                };

                nprogress.start();
                this.isSaving = true;

                try {
                    await this.$store.dispatch('useApiData', {
                        name: 'SaveOrganizationUrlConnection',
                        data: dataToSave
                    });
                    this.isSaving = false;
                    // show result
                    this.showToast({
                        message: this.$t('urlConnectionLicenseSavedConfirmation'),
                        variant: 'success',
                        autoHideDelay: 2500
                    });
                    nprogress.done();
                } catch (error) {
                    nprogress.done();
                    this.isSaving = false;
                    this.showToast({
                        title: this.$t('saveFailureTitle'),
                        message: error.message,
                        variant: 'danger',
                        autoHideDelay: 10000
                    });
                }
            },

            $_onSubmit() {
                if (this.linkTextExists) {
                    this.showToast({
                        message: this.$t('incorrectInputAllFields'),
                        variant: 'danger',
                        noCloseButton: true,
                        autoHideDelay: 8000
                    });
                } else if (this.$refs.connectionLicenseForm.checkValidity()) {
                    this.$_onSave();
                }
            }
        }
    };
</script>
